import React, { useEffect, useState } from "react";
import { FaArrowLeft } from "react-icons/fa";
import "../assets/css/EditTenant.css";
import "../assets/css/DigixLeads.css";
import "../assets/css/Accounts.css";
import "../assets/css/AddUser.css";
import axiosInstance from "../components/api/axiosInstance";
import CountryCodeSelect from "../components/CountryCodeSelect";
import { getAuthItem } from "../utils/authStorage";
import {
  DEFAULT_COUNTRY_CODE,
  formValueWithMobileSanitize,
  mobileValidationMessage,
} from "../utils/phone";
import PasswordInput from "../components/PasswordInput";

function EditAccounts({ accountId, onBack, onUpdate }) {
  const [formData, setFormData] = useState({
    platformName: "",
    accountName: "",
    username: "",
    password: "",
    emailUsed: "",
    countryCode: DEFAULT_COUNTRY_CODE,
    contactNumber: "",
    notes: "",
    url: ""
  });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchAccount = async () => {
      setLoading(true);
      try {
        const response = await axiosInstance.get(`/accounts/${accountId}`);
        const account = response.data?.Account_Id
          ? response.data
          : (response.data?.data || response.data);
        if (account) {
          setFormData({
            platformName: account.Platform_Name || account.platform_name || "",
            accountName: account.Account_Name || account.account_name || "",
            username: account.Username || account.username || "",
            password: account.Password || account.password || "",
            emailUsed: account.Email_Used || account.email_used || "",
            countryCode: account.Country_Code || DEFAULT_COUNTRY_CODE,
            contactNumber: account.Contact_Number || account.contact_number || "",
            notes: account.Notes || account.notes || "",
            url: account.URL || account.url || account.Business_URL || account.business_url || ""
          });
        }
      } catch (err) {
        console.error("Error fetching account:", err);
        alert("Failed to load account details. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    fetchAccount();
  }, [accountId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: formValueWithMobileSanitize(name, value) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.platformName || !formData.accountName || !formData.username) {
      alert("Please fill in required fields (Platform Name, Account Name, Username)");
      return;
    }
    const contactError = mobileValidationMessage(formData.contactNumber, { label: "Contact number" });
    if (contactError) {
      alert(contactError);
      return;
    }

    let updatedBy = 1;
    try {
      const userStr = getAuthItem("user");
      if (userStr) {
        const user = JSON.parse(userStr);
        updatedBy = user.user_id || user.id || 1;
      }
    } catch (err) {
      console.error("Error reading user for Updated_By:", err);
    }

    const payload = {
      Platform_Name: formData.platformName.trim(),
      Account_Name: formData.accountName.trim(),
      Username: formData.username.trim(),
      Password: formData.password || null,
      Email_Used: formData.emailUsed?.trim() || null,
      Country_Code: formData.contactNumber?.trim() ? formData.countryCode : null,
      Contact_Number: formData.contactNumber?.trim() || null,
      Notes: formData.notes?.trim() || null,
      URL: formData.url?.trim() || null,
      Updated_By: updatedBy
    };

    setSubmitting(true);
    try {
      let response;
      try {
        // Primary endpoint for standalone Accounts module updates.
        response = await axiosInstance.put(`/update-account/${accountId}`, payload);
      } catch {
        // Backward-compatible fallback.
        response = await axiosInstance.put(`/accounts/${accountId}`, payload);
      }
      if (response?.data) {
        alert("Account updated successfully");
        if (onUpdate) onUpdate();
        if (onBack) onBack();
      } else {
        throw new Error("Update failed - no response data");
      }
    } catch (err) {
      console.error("Error updating account:", err);
      const msg = err.response?.data?.message || err.response?.data?.error || "Failed to update account.";
      alert(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="edittenant-container"><div className="edittenant-loading">Loading account details...</div></div>;
  }

  return (
    <div className="edittenant-container">
      <button className="back-link" onClick={onBack}><FaArrowLeft /> Back</button>
      <form className="edittenant-form" onSubmit={handleSubmit}>
        <div className="form-section">
          <div className="form-grid">
            <div className="form-group"><label>Platform Name <span className="required">*</span></label><input type="text" name="platformName" value={formData.platformName} onChange={handleInputChange} required /></div>
            <div className="form-group"><label>Account Name <span className="required">*</span></label><input type="text" name="accountName" value={formData.accountName} onChange={handleInputChange} required /></div>
            <div className="form-group"><label>Username <span className="required">*</span></label><input type="text" name="username" value={formData.username} onChange={handleInputChange} required /></div>
            <div className="form-group"><label>Password</label><PasswordInput name="password" value={formData.password} onChange={handleInputChange} /></div>
            <div className="form-group"><label>Email Used</label><input type="email" name="emailUsed" value={formData.emailUsed} onChange={handleInputChange} /></div>
            <div className="form-group">
              <label>Contact Number</label>
              <div className="adduser-phone-row">
                <CountryCodeSelect
                  value={formData.countryCode}
                  onChange={(code) => setFormData((prev) => ({ ...prev, countryCode: code }))}
                  ariaLabel="Contact number country code"
                />
                <input
                  type="tel"
                  name="contactNumber"
                  value={formData.contactNumber}
                  onChange={handleInputChange}
                  inputMode="numeric"
                />
              </div>
            </div>
            <div className="form-group"><label>URL</label><input type="url" name="url" value={formData.url} onChange={handleInputChange}  /></div>
            <div className="form-group"><label>Notes</label><textarea name="notes" value={formData.notes} onChange={handleInputChange} rows="3" /></div>
          </div>
        </div>
        <div className="edittenant-actions">
          <button type="submit" className="btn-subproject-primary" disabled={submitting}>
            {submitting ? "Updating..." : "Update Account"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default EditAccounts;
